import { useState, useEffect } from 'react';
import { DndContext, useDraggable, useDroppable, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import { Trophy, Zap, Clock, Check } from 'lucide-react';

import './Exercise.css';
import './MatchingExercise.css';

// <<< 1. FUNCIÓN DE MEZCLA (SHUFFLE) >>>
// Implementación del algoritmo Fisher-Yates para mezclar arrays in-place
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

function DraggableWord({ id, content }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="me-draggable-word">
      {content}
    </div>
  );
}

function DroppableBox({ id, title, children, isShaking, isFlashing, isCompleted }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const classNames = `me-droppable-box ${isOver ? 'over' : ''} ${isShaking ? 'shake' : ''} ${isFlashing ? 'flash-correct' : ''} ${isCompleted ? 'completed' : ''}`;
  
  return (
    <div ref={setNodeRef} className={classNames}>
      <div className="me-droppable-box-title">{title}</div>
      <hr className="me-separator" />
      <div className="me-dropped-words-container">{children}</div>
    </div>
  );
}

function MatchingExercise({ exerciseData, onCorrectAnswer }) {
  const { title, instruction, pairs = [], matches = [], solution = {} } = exerciseData;

  const [placedWords, setPlacedWords] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(exerciseData.timeLimit || 60);
  const [feedbackState, setFeedbackState] = useState({});
  // Estado para guardar el array de palabras mezcladas una sola vez
  const [shuffledPairs, setShuffledPairs] = useState([]);


  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const solutionMap = Object.entries(solution).reduce((acc, [wordId, categoryId]) => {
    if (!acc[categoryId]) acc[categoryId] = [];
    acc[categoryId].push(wordId);
    return acc;
  }, {});
  
  // <<< 2. MEZCLA AL INICIO >>>
  useEffect(() => {
    // Clonamos y mezclamos el array 'pairs' y lo guardamos.
    // Usamos pairs.length para asegurar que la mezcla solo ocurra al cargar nuevos datos.
    setShuffledPairs(shuffleArray([...pairs]));
  }, [pairs.length]);


  useEffect(() => {
    if (isCompleted) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isCompleted]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active) {
      const wordId = active.id;
      const boxId = over.id;

      if (solutionMap[boxId]?.includes(wordId)) {
        setPlacedWords(prev => {
          const newPlaced = { ...prev };
          if (!newPlaced[boxId]) newPlaced[boxId] = [];
          if (!newPlaced[boxId].includes(wordId)) {
            newPlaced[boxId].push(wordId);
            setPoints(p => p + 10);
            setStreak(s => s + 1);
          }
          return newPlaced;
        });
        setFeedbackState({ [boxId]: 'flash' });
      } else {
        setStreak(0);
        setFeedbackState({ [boxId]: 'shake' });
      }
      
      setTimeout(() => setFeedbackState({}), 500);
    }
  };

  useEffect(() => {
    if (isCompleted) return;
    const totalPlaced = Object.values(placedWords).flat().length;
    if (totalPlaced === pairs.length && pairs.length > 0) {
      setIsCompleted(true);
      onCorrectAnswer();
      showAchievement('Perfect Match!', '🔗');
      launchConfetti();
    }
  }, [placedWords, pairs, onCorrectAnswer, isCompleted]);

  const getWordById = (id) => pairs.find(p => p.id === id);
  
  // Usamos la lista mezclada (shuffledPairs) para determinar qué palabras están disponibles
  const availableWords = shuffledPairs.filter(p => !Object.values(placedWords).flat().includes(p.id));

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="exercise-container">
        <div className="me-info-bar">
          <div className="me-info-item"><span>🏆</span> {points} pts</div>
          <div className="me-info-sub-bar">
            <div className="me-info-item"><span>⚡</span> Racha: {streak}</div>
            <div className={`me-info-item ${timeLeft <= 10 ? 'accent-bg' : ''}`}><span>🕒</span> {timeLeft}s</div>
          </div>
        </div>
        
        <h2 className="exercise-title">{title || 'Vocabulary Matching'}</h2>
        <p className="exercise-instruction">{instruction}</p>

        <div className="me-matching-container">
          <div className="me-droppables-row">
            {matches.map(match => {
              const placedInBox = placedWords[match.id] || [];
              const requiredInBox = solutionMap[match.id] || [];
              const isBoxCompleted = requiredInBox.length > 0 && placedInBox.length === requiredInBox.length;

              return (
                <DroppableBox 
                  key={match.id} 
                  id={match.id} 
                  title={match.content}
                  isShaking={feedbackState[match.id] === 'shake'}
                  isFlashing={feedbackState[match.id] === 'flash'}
                  isCompleted={isBoxCompleted}
                >
                  {placedInBox.map(wordId => (
                    <div key={wordId} className="me-draggable-word placed correct">
                      <Check size={16} strokeWidth={3} />
                      {getWordById(wordId)?.content}
                    </div>
                  ))}
                </DroppableBox>
              )
            })}
          </div>

          <div className="me-words-bank">
            <h3 className="me-words-bank-title">Word Bank</h3>
            <div className="me-words-row">
              {availableWords.map(word => (
                <DraggableWord key={word.id} id={word.id} content={word.content} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}

export default MatchingExercise;